import React, { useRef, useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  IconButton,
  Slider,
  Stack,
  Tooltip,
  Alert,
  CircularProgress,
  TextField,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip
} from '@mui/material';
import { 
  Upload as UploadIcon, 
  PlayArrow, 
  Pause,
  VolumeUp,
  VolumeOff,
  ContentCut,
  Download,
  TextFields,
  Search,
  CheckCircle,
  Edit,
  Mic
} from '@mui/icons-material';
import WaveSurfer from 'wavesurfer.js';
import RegionsPlugin from 'wavesurfer.js/dist/plugins/regions';
import RecordPlugin from 'wavesurfer.js/dist/plugins/record.js';

interface Region {
  start: number;
  end: number;
  label: string;
  text: string;
  isApproved: boolean;
}

interface Word {
  startTime?: { seconds?: string | number; nanos?: number };
  endTime?: { seconds?: string | number; nanos?: number };
  word?: string;
}

interface TranscriptionApiResponse {
  success: boolean;
  transcription?: string;
  words?: Word[];
  error?: string;
  details?: string;
}

interface VOEditorProps {
  voLines: string[]; // Array of VO text lines from VO Manager
}

const SUPPORTED_FORMATS = ['audio/mp3', 'audio/wav', 'audio/mpeg', 'audio/m4a', 'audio/x-m4a', 'audio/aac'];
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export const VOEditor: React.FC<VOEditorProps> = ({ voLines }) => {
  const waveformRef = useRef<HTMLDivElement>(null);
  const wavesurferRef = useRef<WaveSurfer | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState<Region | null>(null);
  const [trimmedAudio, setTrimmedAudio] = useState<Blob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [regions, setRegions] = useState<Region[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showRegionsDialog, setShowRegionsDialog] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);

  // Initialize AudioContext
  useEffect(() => {
    audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    return () => {
      if (audioContextRef.current?.state !== 'closed') {
        audioContextRef.current?.close();
      }
    };
  }, []);

  // Initialize WaveSurfer with Regions plugin
  useEffect(() => {
    if (waveformRef.current) {
      console.log('Initializing WaveSurfer...');
      wavesurferRef.current = WaveSurfer.create({
        container: waveformRef.current,
        waveColor: '#4a4a4a',
        progressColor: '#1976d2',
        cursorColor: '#1976d2',
        barWidth: 2,
        barRadius: 3,
        cursorWidth: 1,
        height: 100,
        barGap: 3,
        plugins: [
          RegionsPlugin.create()
        ]
      });

      wavesurferRef.current.on('play', () => {
        console.log('WaveSurfer: Play event');
        setIsPlaying(true);
      });
      wavesurferRef.current.on('pause', () => {
        console.log('WaveSurfer: Pause event');
        setIsPlaying(false);
      });
      wavesurferRef.current.on('finish', () => {
        console.log('WaveSurfer: Finish event');
        setIsPlaying(false);
      });
      wavesurferRef.current.on('error', (err) => {
        console.error('WaveSurfer error:', err);
        setError(`WaveSurfer error: ${err}`);
      });
      wavesurferRef.current.on('ready', () => {
        console.log('WaveSurfer: Ready event');
      });
      wavesurferRef.current.on('loading', (percent) => {
        console.log('WaveSurfer loading:', percent + '%');
      });

      // Handle region events through the plugin
      const regionsPlugin = wavesurferRef.current.getActivePlugins().find(p => p instanceof RegionsPlugin) as RegionsPlugin;
      if (regionsPlugin) {
        (regionsPlugin as any).on('region-created', (region: any) => {
          console.log('Region created:', region);
          if (region.content) {
            const existingRegion = regions.find(r => r.label === region.content);
            if (existingRegion) {
              setSelectedRegion({
                ...existingRegion,
                start: region.start,
                end: region.end
              });
            }
          }
        });

        (regionsPlugin as any).on('region-updated', (region: any) => {
          console.log('Region updated:', region);
          if (region.content) {
            const existingRegion = regions.find(r => r.label === region.content);
            if (existingRegion) {
              setSelectedRegion({
                ...existingRegion,
                start: region.start,
                end: region.end
              });
            }
          }
        });
      }

      return () => {
        console.log('Destroying WaveSurfer instance');
        wavesurferRef.current?.destroy();
      };
    }
  }, [regions]);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!SUPPORTED_FORMATS.includes(file.type)) {
      setError(`Unsupported file format. Please upload one of: ${SUPPORTED_FORMATS.join(', ')}`);
      return;
    }

    setIsLoading(true);
    setError(null);
    setAudioFile(file);

    try {
      // Create URL for the file
      const url = URL.createObjectURL(file);
      console.log('Created URL for file:', url);

      // Fetch the audio data
      const response = await fetch(url);
      const arrayBuffer = await response.arrayBuffer();
      console.log('Fetched audio data, size:', arrayBuffer.byteLength);

      // Create audio context and decode audio
      const audioContext = audioContextRef.current;
      if (!audioContext) {
        throw new Error('AudioContext not initialized');
      }

      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      console.log('Decoded audio, duration:', audioBuffer.duration);

      // Process audio with limiter
      const processedBuffer = await processAudioWithLimiter(audioBuffer);
      console.log('Processed audio with limiter');

      // Convert to WAV
      const wavBlob = await audioBufferToWav(processedBuffer);
      console.log('Converted to WAV, size:', wavBlob.size);

      // Create URL for processed audio
      const processedUrl = URL.createObjectURL(wavBlob);
      setAudioUrl(processedUrl);

      // Load into WaveSurfer
      if (wavesurferRef.current) {
        await wavesurferRef.current.load(processedUrl);
      }

      setIsLoading(false);
    } catch (err) {
      console.error('Error processing audio:', err);
      setError(`Error processing audio: ${err instanceof Error ? err.message : String(err)}`);
      setIsLoading(false);
    }
  };

  const processAudioWithLimiter = async (audioBuffer: AudioBuffer): Promise<AudioBuffer> => {
    const offlineContext = new OfflineAudioContext({
      numberOfChannels: audioBuffer.numberOfChannels,
      length: audioBuffer.length,
      sampleRate: audioBuffer.sampleRate
    });

    // Create source
    const source = offlineContext.createBufferSource();
    source.buffer = audioBuffer;

    // Create gain node for limiting
    const gainNode = offlineContext.createGain();
    gainNode.gain.value = 0.7079; // -3dB

    // Connect nodes
    source.connect(gainNode);
    gainNode.connect(offlineContext.destination);

    // Start rendering
    source.start(0);
    return await offlineContext.startRendering();
  };

  const audioBufferToWav = async (buffer: AudioBuffer): Promise<Blob> => {
    const numberOfChannels = buffer.numberOfChannels;
    const length = buffer.length;
    const sampleRate = buffer.sampleRate;
    const bitsPerSample = 16;
    const bytesPerSample = bitsPerSample / 8;
    const blockAlign = numberOfChannels * bytesPerSample;
    const byteRate = sampleRate * blockAlign;
    const dataSize = length * blockAlign;

    const arrayBuffer = new ArrayBuffer(44 + dataSize);
    const view = new DataView(arrayBuffer);

    // Write WAV header
    const writeString = (offset: number, string: string) => {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    };

    writeString(0, 'RIFF');                     // ChunkID
    view.setUint32(4, 36 + dataSize, true);     // ChunkSize
    writeString(8, 'WAVE');                     // Format
    writeString(12, 'fmt ');                    // Subchunk1ID
    view.setUint32(16, 16, true);               // Subchunk1Size
    view.setUint16(20, 1, true);                // AudioFormat (PCM)
    view.setUint16(22, numberOfChannels, true); // NumChannels
    view.setUint32(24, sampleRate, true);       // SampleRate
    view.setUint32(28, byteRate, true);         // ByteRate
    view.setUint16(32, blockAlign, true);       // BlockAlign
    view.setUint16(34, bitsPerSample, true);    // BitsPerSample
    writeString(36, 'data');                    // Subchunk2ID
    view.setUint32(40, dataSize, true);         // Subchunk2Size

    // Write audio data
    const offset = 44;
    const channelData = new Array(numberOfChannels).fill(0).map((_, i) => buffer.getChannelData(i));
    
    for (let i = 0; i < length; i++) {
      for (let channel = 0; channel < numberOfChannels; channel++) {
        const sample = Math.max(-1, Math.min(1, channelData[channel][i]));
        const value = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
        view.setInt16(offset + (i * blockAlign) + (channel * bytesPerSample), value, true);
      }
    }

    return new Blob([arrayBuffer], { type: 'audio/wav' });
  };

  const handlePlayPause = () => {
    if (wavesurferRef.current) {
      if (isPlaying) {
        wavesurferRef.current.pause();
      } else {
        wavesurferRef.current.play();
      }
    }
  };

  const handleVolumeChange = (_event: Event, newValue: number | number[]) => {
    const value = newValue as number;
    setVolume(value);
    if (wavesurferRef.current) {
      wavesurferRef.current.setVolume(value);
    }
  };

  const handleMuteToggle = () => {
    setIsMuted(!isMuted);
    if (wavesurferRef.current) {
      wavesurferRef.current.setVolume(isMuted ? volume : 0);
    }
  };

  const blobToBase64 = (blob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result?.toString().split(',')[1]; // Remove the data URL prefix
        if (base64String) {
          resolve(base64String);
        } else {
          reject(new Error('Failed to convert blob to base64'));
        }
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const timeToSeconds = (time: { seconds?: string | number; nanos?: number } | undefined): number => {
    if (!time) return 0;
    const seconds = parseFloat(time.seconds?.toString() || '0');
    const nanos = time.nanos ? time.nanos / 1e9 : 0;
    return seconds + nanos;
  };

  const processVOLines = async () => {
    if (!audioUrl || !wavesurferRef.current) return;

    setIsProcessing(true);
    setError(null);
    setRegions([]); // Clear existing regions before processing

    try {
      // Fetch the processed WAV Blob using the audioUrl
      const response = await fetch(audioUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch processed audio: ${response.statusText}`);
      }
      const wavBlob = await response.blob();

      // Convert Blob to base64
      const audioBase64 = await blobToBase64(wavBlob);

      console.log('Sending audio to backend for transcription...');
      // Send base64 audio data to the backend API using fetch instead of axios
      const backendResponse = await fetch(`${API_BASE_URL}/api/transcribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          audioBytes: audioBase64,
          encoding: 'LINEAR16', // Assuming WAV format after conversion
          sampleRateHertz: audioContextRef.current?.sampleRate || 44100, // Use actual sample rate
          languageCode: 'en-US' // Or make this configurable
        })
      });

      if (!backendResponse.ok) {
        throw new Error(`Transcription failed: ${backendResponse.statusText}`);
      }

      const responseData = await backendResponse.json();

      if (responseData.success && responseData.words) {
        console.log('Transcription received from backend:', responseData.transcription);
        const words = responseData.words;

        // Clear previous regions visually
        const wsRegions = wavesurferRef.current.getActivePlugins().find(p => p instanceof RegionsPlugin) as RegionsPlugin;
        wsRegions?.clearRegions();

        // Create regions based on words and original voLines
        const newRegions: Region[] = [];
        let wordIndex = 0;
        voLines.forEach((line, lineIndex) => {
          const lineWords = line.trim().split(/\s+/);
          if (lineWords.length === 0 || wordIndex >= words.length) return;

          const startWord = words[wordIndex];
          let endWord = startWord;
          let matchCount = 0;

          // Find the sequence of words matching the line
          for (let i = 0; i < lineWords.length && wordIndex + i < words.length; i++) {
            // Basic comparison (can be improved for punctuation, case, etc.)
            if (words[wordIndex + i].word?.toLowerCase() === lineWords[i].toLowerCase().replace(/[.,!?;:]/g, '')) {
              endWord = words[wordIndex + i];
              matchCount++;
            } else {
              break; // Word sequence doesn't match
            }
          }

          if (matchCount === lineWords.length) { // Only create region if all words matched
            const startTime = timeToSeconds(startWord.startTime);
            const endTime = timeToSeconds(endWord.endTime);

            if (wavesurferRef.current) {
              wsRegions?.addRegion({
                start: startTime,
                end: endTime,
                content: `Line ${lineIndex + 1}`,
                color: 'rgba(0, 100, 0, 0.1)', // Example color
              });

              newRegions.push({
                start: startTime,
                end: endTime,
                label: `Line ${lineIndex + 1}`,
                text: line,
                isApproved: false
              });
            }
            wordIndex += lineWords.length; // Move index past the matched words
          } else {
             console.warn(`Could not accurately match line ${lineIndex + 1}: "${line}" with transcribed words.`);
             // Optionally skip words if matching fails to avoid cascading errors
             // wordIndex++; // Or a more sophisticated skip logic
          }
        });

        setRegions(newRegions);
        setShowRegionsDialog(true);
      } else {
        throw new Error(responseData.details || responseData.error || 'Transcription failed on backend');
      }

      setIsProcessing(false);
    } catch (err) {
      console.error('Error processing VO lines:', err);
      setError(`Error processing VO lines: ${err instanceof Error ? err.message : String(err)}`);
      setIsProcessing(false);
    }
  };

  const handleApproveRegion = (label: string) => {
    setRegions(prevRegions => 
      prevRegions.map(region => 
        region.label === label 
          ? { ...region, isApproved: !region.isApproved }
          : region
      )
    );
  };

  const exportRegion = async (region: Region) => {
    if (!wavesurferRef.current || !region.isApproved) return;

    try {
      const audioContext = audioContextRef.current;
      if (!audioContext) {
        throw new Error('AudioContext not initialized');
      }

      // Get the audio data for the region
      const originalBuffer = wavesurferRef.current.getDecodedData();
      if (!originalBuffer) {
        throw new Error('No audio data available');
      }

      const startSample = Math.floor(region.start * originalBuffer.sampleRate);
      const endSample = Math.floor(region.end * originalBuffer.sampleRate);
      const length = endSample - startSample;

      const trimmedBuffer = audioContext.createBuffer(
        originalBuffer.numberOfChannels,
        length,
        originalBuffer.sampleRate
      );

      // Copy the audio data for the region
      for (let channel = 0; channel < originalBuffer.numberOfChannels; channel++) {
        const channelData = originalBuffer.getChannelData(channel);
        const trimmedData = trimmedBuffer.getChannelData(channel);
        for (let i = 0; i < length; i++) {
          trimmedData[i] = channelData[startSample + i];
        }
      }

      // Convert to WAV and download
      const wavBlob = await audioBufferToWav(trimmedBuffer);
      const url = URL.createObjectURL(wavBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${region.label.replace(/\s+/g, '_')}.wav`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting region:', err);
      setError(`Error exporting region: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const exportAllRegions = () => {
    regions.filter(region => region.isApproved).forEach(exportRegion);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          VO Editor
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {/* Audio Upload Section */}
        <Box sx={{ mb: 3 }}>
          <input
            accept="audio/*"
            style={{ display: 'none' }}
            id="audio-upload"
            type="file"
            onChange={handleFileUpload}
          />
          <label htmlFor="audio-upload">
            <Button
              variant="contained"
              component="span"
              startIcon={<UploadIcon />}
              disabled={isLoading}
            >
              {isLoading ? 'Loading...' : 'Upload Audio'}
            </Button>
          </label>
          {audioFile && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Selected file: {audioFile.name}
            </Typography>
          )}
        </Box>

        {/* Waveform Section */}
        <Box sx={{ mb: 3, position: 'relative' }}>
          {isLoading && (
            <Box sx={{ 
              position: 'absolute', 
              top: 0, 
              left: 0, 
              right: 0, 
              bottom: 0, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              zIndex: 1
            }}>
              <CircularProgress />
            </Box>
          )}
          <div ref={waveformRef} style={{ width: '100%' }} />
          
          {/* Controls Section */}
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>
            <IconButton
              onClick={handlePlayPause}
              disabled={!audioUrl || isLoading}
              color="primary"
              size="large"
            >
              {isPlaying ? <Pause /> : <PlayArrow />}
            </IconButton>

            <Stack direction="row" spacing={1} alignItems="center" sx={{ width: 200 }}>
              <IconButton onClick={handleMuteToggle} size="small" disabled={isLoading}>
                {isMuted ? <VolumeOff /> : <VolumeUp />}
              </IconButton>
              <Slider
                value={volume}
                onChange={handleVolumeChange}
                min={0}
                max={1}
                step={0.01}
                disabled={isMuted || isLoading}
              />
            </Stack>

            <Tooltip title="Process VO Lines">
              <span>
                <IconButton
                  onClick={processVOLines}
                  disabled={!audioUrl || isLoading || isProcessing}
                  color="primary"
                >
                  <TextFields />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Export All Approved Regions">
              <span>
                <IconButton
                  onClick={exportAllRegions}
                  disabled={!regions.some(r => r.isApproved) || isLoading}
                  color="primary"
                >
                  <Download />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Box>

        {/* Regions Dialog */}
        <Dialog 
          open={showRegionsDialog} 
          onClose={() => setShowRegionsDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>VO Regions</DialogTitle>
          <DialogContent>
            <List>
              {regions.map((region, index) => (
                <ListItem key={index}>
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="subtitle1">{region.label}</Typography>
                        {region.isApproved && <CheckCircle color="success" fontSize="small" />}
                      </Stack>
                    }
                    secondary={
                      <Stack spacing={1}>
                        <Typography variant="body2">{region.text}</Typography>
                        <Typography variant="caption">
                          {region.start.toFixed(2)}s - {region.end.toFixed(2)}s
                        </Typography>
                      </Stack>
                    }
                  />
                  <Stack direction="row" spacing={1}>
                    <IconButton
                      size="small"
                      onClick={() => handleApproveRegion(region.label)}
                      color={region.isApproved ? "success" : "default"}
                    >
                      <CheckCircle />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => exportRegion(region)}
                      disabled={!region.isApproved}
                    >
                      <Download />
                    </IconButton>
                  </Stack>
                </ListItem>
              ))}
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowRegionsDialog(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Box>
  );
}; 